/**
 * Shell-safe seam between the always-loaded global keyboard shortcuts
 * and the lazily-loaded `BottomToolbar`. Lives in its own module so
 * `keyboardShortcuts.ts` doesn't statically import `BottomToolbar`,
 * which would pin the toolbar into the app-shell chunk and defeat the
 * dynamic `import('@/blackbolt/BottomToolbar')` in `AppView.tsx`.
 */

import type { Unsubscribe } from '@amazon/vinyl-util'

// Module-scoped so the global `/` shortcut can reach the input without
// threading a ref through app root; there's at most one BottomToolbar
// mounted at a time, so a single slot is sufficient. The composer is a
// `<textarea>` (multiline), but this seam only ever calls `.focus()`, so
// the union keeps callers free to pass either control.
let chatInputEl: HTMLInputElement | HTMLTextAreaElement | null = null
let clearComposer: (() => void) | null = null

/**
 * Register the chat input element. Returns a disposer that clears the
 * slot unless another input has since claimed it.
 */
export function registerChatInput(el: HTMLInputElement | HTMLTextAreaElement): Unsubscribe {
    chatInputEl = el
    return () => {
        if (chatInputEl === el) chatInputEl = null
    }
}

/**
 * Focus the agent chat input. On the shell variant the toolbar's
 * container-level `focusin` handler expands the pill; on `/chat` the
 * row is already pinned. Wired to the global `/` shortcut.
 */
export function focusAgentChat(): void {
    chatInputEl?.focus()
}

/**
 * Register the composer's clear routine (BottomToolbar owns `inputText$` +
 * the textarea, so the reset lives there). Lets surfaces outside the toolbar
 * — the quick-search overlay on result-select — empty the composer without a
 * ref, so returning to chat shows a blank pill rather than the stale search
 * term.
 */
export function registerChatComposerClear(fn: () => void): Unsubscribe {
    clearComposer = fn
    return () => {
        if (clearComposer === fn) clearComposer = null
    }
}

/** Clear the composer text (no-op when the toolbar isn't mounted). */
export function clearChatComposer(): void {
    clearComposer?.()
}
